import SelectMissionModal from '../../Robot/RobotView/RobotLeftBlock/RobotDetails/RobotMissionMainStatus/SelectMissionModal/SelectMissionModal'
import SelectRobotModal from '../../Robot/RobotView/RobotLeftBlock/RobotDetails/RobotMissionMainStatus/SelectRobotModal/SelectRobotModal'
import { toISOStringWithTimezone, toISOStringYYYYMMDD } from '../TaskSchedulerUtils'
import { Tabs } from '@mui/material'
import { MainApi } from 'api/MainApi'
import { RobotApi } from 'api/RobotApi'
import {
  MainService,
  MissionJobRead,
  MissionJobTypeEnum,
  OdysseyRead,
  RobotRead,
  SchedulerSettings as SchedulerSettingsType,
} from 'api/schema'
import clsx from 'clsx'
import { Button } from 'components/Button/Button'
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog'
import { StyledTab } from 'components/StyledTab/StyledTab'
import TabPanel from 'components/TabPanel/TabPanel'
import TextFieldDescribed from 'components/TextFieldDescribed/TextFieldDescribed'
import Title from 'components/Title/Title'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Modal from 'react-modal'
import { MISSION_CALCULATING_STATUSES } from 'utils/constants'
import { MISSION_JOB_STATUSES } from 'utils/constants'
import {
  DIAGNOSTIC_LEVEL,
  createDiagnosticStatusClassGetter,
  getDiagnosticStatusText,
  shouldShowDiagnostics as shouldShowDiagnosticsUtil,
} from 'utils/diagnosticsUtils'
import { IMissionInfo } from 'utils/interfaces'
import { VENDOR } from 'utils/vendorDisplayName'

import SchedulerSettings from './SchedulerSettings/SchedulerSettings'

import diagnosticStyles from '../../Robot/RobotView/RobotLeftBlock/RobotDetails/RobotDetails.module.css'
import style from './TaskSettingsModal.module.css'

// Robot diagnostics polling interval (in milliseconds)
const ROBOT_DIAGNOSTICS_POLLING_INTERVAL = 5000 // 5 seconds

interface IDialogProps {
  isOpen: boolean
  taskName: string
  onClose: (refresh: boolean) => void
  onSubmit: (refresh: boolean) => void
  taskToEdit: MissionJobRead | OdysseyRead | undefined
  eventScope?: string
  dateToEdit: Date | null
  initialLaunchItem?: { scenario?: any; mission?: IMissionInfo; task?: MissionJobRead | OdysseyRead }
  robotIdsFilter?: number[]
  initialTab?: number
}

const TaskSettingsModal = ({
  isOpen,
  taskName,
  onClose,
  onSubmit,
  taskToEdit,
  eventScope,
  dateToEdit,
  initialLaunchItem,
  robotIdsFilter,
  initialTab,
}: React.PropsWithChildren<IDialogProps>) => {
  const { t } = useTranslation()
  const [tempTaskName, setTempTaskName] = useState(taskName)
  const [hasNameError, setNameHasError] = useState(false)
  const [showMissionModal, setShowMissionModal] = useState(false)
  const [showRobotModal, setShowRobotModal] = useState(false)
  const [selectedMission, setSelectedMission] = useState<IMissionInfo | any | undefined>(undefined)
  const [selectedScenario, setSelectedScenario] = useState<any | undefined>(undefined)
  const [selectedRobot, setSelectedRobot] = useState<RobotRead | undefined>(undefined)
  const [tab, setTab] = useState(0)
  const [date, setDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)
  const [selectedWeekdays, setSelectedWeekdays] = useState<boolean[]>([false, false, false, false, false, false, false])
  const [robot_type, setRobotType] = useState<string | undefined>(undefined)

  useEffect(() => {
    if (selectedRobot?.robot_type) {
      setRobotType(selectedRobot.robot_type)
    } else {
      const mission = selectedMission || selectedScenario
      if (mission?.robot_type) {
        setRobotType(mission.robot_type)
      } else {
        setRobotType(undefined)
      }
    }
  }, [selectedRobot, selectedMission, selectedScenario])

  // Check if robot has blocking diagnostics (ERROR or STALE)
  const hasBlockingDiagnostics = () => {
    const level = selectedRobot?.system_diagnostics?.level
    return level === DIAGNOSTIC_LEVEL.ERROR || level === DIAGNOSTIC_LEVEL.STALE
  }

  // Check if task is instant (no date/time scheduled)
  const isInstantTask = () => {
    return !date && areArraysEqual(selectedWeekdays, [false, false, false, false, false, false, false])
  }

  // Check if save should be blocked due to diagnostics
  const shouldBlockSave = () => {
    return hasBlockingDiagnostics() && isInstantTask()
  }

  const isSaveButtonDisabled =
    tempTaskName === '' ||
    hasNameError ||
    (!selectedMission && !selectedScenario) ||
    !selectedRobot ||
    (!date && !areArraysEqual(selectedWeekdays, [false, false, false, false, false, false, false])) ||
    (selectedMission && selectedMission.robot_type !== selectedRobot.robot_type) ||
    (selectedMission && selectedMission.status !== MISSION_CALCULATING_STATUSES.OK) ||
    shouldBlockSave()
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false)
  const isSingleScope = taskToEdit && eventScope === 'current'
  const isLaunchMode = !!initialLaunchItem

  useEffect(() => {
    if (isOpen) {
      setTab(initialTab ?? 0)
    }
  }, [isOpen, initialTab])

  const fetchJobMission = async (id: number) => {
    try {
      const mission = await MainApi.fetchMission(id)
      setSelectedMission(mission.mission)
    } catch (error) {
      setSelectedMission(undefined)
    }
  }

  const fetchTaskScenario = async (id: number) => {
    try {
      const scenario = await MainApi.fetchScenario(id)
      setSelectedScenario(scenario)
    } catch (error) {
      setSelectedScenario(undefined)
    }
  }

  const fetchOdysseyMissions = async (id: number[]) => {
    try {
      setSelectedMission(undefined)
      if (id.length > 0) {
        const mission = await MainApi.fetchMission(id[0])
        setSelectedMission(mission.mission)
      }
      // setSelectedMissions(prev => [...prev, undefined])
    } catch (error) {
      setSelectedMission(undefined)
    }
  }

  const fetchJobRobot = async (id: number) => {
    try {
      const robot = await MainService.mainRobotRetrieve(id)
      setSelectedRobot(robot)
    } catch (error) {
      setSelectedRobot(undefined)
    }
  }

  // const onRemoveMissionClick = (index: number) => {
  //   const updatedMissions = selectedMissions.filter((_, i) => i !== index)
  //   setSelectedMissions(updatedMissions)
  // }

  // Prepare data for the edit case
  useEffect(() => {
    if (isOpen && (taskToEdit || initialLaunchItem?.task)) {
      const task = (initialLaunchItem?.task || taskToEdit) as MissionJobRead | OdysseyRead
      setNameHasError(false)
      setTempTaskName(task.name || '')
      if ('scenario' in task && task.scenario) {
        if (typeof task.scenario === 'number') {
          fetchTaskScenario(task.scenario)
        } else {
          setSelectedScenario(task.scenario)
        }
        setSelectedMission(undefined)
      } else if ('missions' in task) {
        fetchOdysseyMissions(task.missions)
      }

      fetchJobRobot(task.robot_id)
      setDate(
        isLaunchMode
          ? null
          : dateToEdit
          ? dateToEdit
          : task?.scheduler_settings?.datetime
          ? new Date(task.scheduler_settings.datetime)
          : null
      )
      setEndDate(null)
      setSelectedWeekdays(
        !isLaunchMode && task?.scheduler_settings?.weekdays
          ? [
              task.scheduler_settings.weekdays.SU === 'true',
              task.scheduler_settings.weekdays.MO === 'true',
              task.scheduler_settings.weekdays.TU === 'true',
              task.scheduler_settings.weekdays.WE === 'true',
              task.scheduler_settings.weekdays.TH === 'true',
              task.scheduler_settings.weekdays.FR === 'true',
              task.scheduler_settings.weekdays.SA === 'true',
            ]
          : [false, false, false, false, false, false, false]
      )
    } else if (isOpen) {
      // a general case - creating new task or launch
      setSelectedRobot(undefined)
      setSelectedMission(undefined)
      if (initialLaunchItem?.scenario) {
        setSelectedScenario(initialLaunchItem.scenario)
        setTempTaskName(initialLaunchItem.scenario.name || '')
      } else if (initialLaunchItem?.mission) {
        setSelectedMission(initialLaunchItem.mission)
        setSelectedScenario(undefined)
        setTempTaskName(initialLaunchItem.mission.name || '')
      } else {
        setSelectedScenario(undefined)
        setTempTaskName(taskName)
      }
      setDate(null)
      setEndDate(null)
      setSelectedWeekdays([false, false, false, false, false, false, false])
      setNameHasError(false)
    }
  }, [isOpen, taskToEdit, initialLaunchItem, dateToEdit, taskName])

  const onTaskNameChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setTempTaskName(value)
    setNameHasError(value === '')
  }

  const handleSchedulerChange = async (
    newDate: Date | null,
    newEndDate: Date | null,
    newSelectedWeekdays: boolean[]
  ) => {
    setDate(newDate)
    setEndDate(newEndDate)
    setSelectedWeekdays(newSelectedWeekdays)
  }

  function areArraysEqual(arr1: boolean[], arr2: boolean[]): boolean {
    if (arr1.length !== arr2.length) return false
    return arr1.every((value, index) => value === arr2[index])
  }

  const isOdyssey = (): boolean => {
    return taskToEdit ? 'missions' in taskToEdit : !!selectedScenario
  }

  const createTask = async (
    missionIds: number[],
    robotId: number,
    schedulerSettings: SchedulerSettingsType,
    name: string,
    scenarioId?: number | null
  ) => {
    await RobotApi.createOdyssey(missionIds.slice(0, 1), robotId, schedulerSettings, name, scenarioId)
  }

  const updateTask = async (
    taskId: number,
    missionIds: number[],
    robotId: number,
    schedulerSettings: SchedulerSettingsType,
    name: string,
    scenarioId?: number | null
  ) => {
    if (isOdyssey()) {
      await RobotApi.updateTheEntireOdyssey(
        taskId,
        missionIds.slice(0, 1),
        robotId,
        schedulerSettings,
        name,
        scenarioId,
        MISSION_JOB_STATUSES.NEW
      )
    } else {
      await RobotApi.updateTheEntireJob(taskId, missionIds[0], robotId, schedulerSettings, name)
    }
  }

  const updateSchedule = async (taskId: number, schedulerSettings: SchedulerSettingsType) => {
    if (isOdyssey()) await MainApi.updateOdysseySchedule(taskId, schedulerSettings)
    else await MainApi.updateMissionJobSchedule(taskId, schedulerSettings)
  }

  const deleteTask = async (taskId: number) => {
    if (isOdyssey()) await RobotApi.updateOdysseyType(taskId, MissionJobTypeEnum.DELETE_SCENARIO)
    else await RobotApi.updateMissionJobType(taskId, MissionJobTypeEnum.DELETE_SCENARIO)
  }

  // The main logic to save or edit a task
  const executeSave = async () => {
    // sanity check
    if ((!selectedMission && !selectedScenario) || !selectedRobot) {
      return
    }

    // extract mission IDs
    let missionIds: number[] = []
    if (!selectedScenario && selectedMission) {
      missionIds = [selectedMission.id]
    }

    // prepare scheduler settings
    let weekdays = null
    if (!areArraysEqual(selectedWeekdays, [false, false, false, false, false, false, false])) {
      weekdays = selectedWeekdays
        ? {
            SU: selectedWeekdays[0].toString(),
            MO: selectedWeekdays[1].toString(),
            TU: selectedWeekdays[2].toString(),
            WE: selectedWeekdays[3].toString(),
            TH: selectedWeekdays[4].toString(),
            FR: selectedWeekdays[5].toString(),
            SA: selectedWeekdays[6].toString(),
          }
        : null
    }
    const currentSettings = taskToEdit?.scheduler_settings
    let schedulerSettings: SchedulerSettingsType = {
      datetime: date ? toISOStringWithTimezone(date) : null,
      weekdays,
      except_days: currentSettings ? currentSettings.except_days : [],
      except_days_descriptions: currentSettings?.except_days_descriptions || {},
      except_days_users: currentSettings?.except_days_users || {},
    }

    try {
      // A general case: just create a new task
      if (!taskToEdit) {
        await createTask(missionIds, selectedRobot.id, schedulerSettings, tempTaskName, selectedScenario?.id)
        return
      }

      // We have only one specific case: editing of the single event of a recurring task
      if (isSingleScope) {
        // if there is a recurring task, exclude this date and create a new single task
        if (taskToEdit.scheduler_settings?.weekdays && date && dateToEdit) {
          // create a new scheduler settings with the excepted date added
          let updated_scheduler_settings = {
            ...taskToEdit.scheduler_settings,
            except_days: [...taskToEdit.scheduler_settings.except_days],
          }
          updated_scheduler_settings.except_days.push(toISOStringYYYYMMDD(dateToEdit))
          updated_scheduler_settings.except_days.sort()

          // reset scheduler settings for a new task
          schedulerSettings = {
            datetime: date ? toISOStringWithTimezone(date) : null,
            weekdays: null,
            except_days: [],
          }
          await updateSchedule(taskToEdit.id, updated_scheduler_settings)
          await createTask(missionIds, selectedRobot.id, schedulerSettings, tempTaskName, selectedScenario?.id)
          console.debug(
            `Excluding event date ${dateToEdit} from the task with id ${taskToEdit.id} and creating a new single task for this date`
          )
          return
        }
      }
      // The common case for editing the entire task
      if (selectedRobot?.id !== taskToEdit.robot_id) {
        await deleteTask(taskToEdit.id)
        await createTask(missionIds, selectedRobot.id, schedulerSettings, tempTaskName, selectedScenario?.id)
      } else {
        await updateTask(
          taskToEdit.id,
          missionIds,
          taskToEdit.robot_id,
          schedulerSettings,
          tempTaskName,
          selectedScenario?.id
        )
      }
    } catch (error) {
      console.log('Error saving task: ', error)
    }
  }

  const onSaveClicked = async () => {
    if (!date) setIsConfirmationOpen(true)
    else {
      await executeSave()
      onSubmit(true)
      resetData()
    }
  }

  const handleConfirmSave = async () => {
    setIsConfirmationOpen(false)
    await executeSave()
    onSubmit(true)
    resetData()
  }

  const resetData = () => {
    setSelectedRobot(undefined)
    setSelectedMission(undefined)
    setSelectedScenario(undefined)
    setDate(null)
    setSelectedWeekdays([false, false, false, false, false, false, false])
    setEndDate(null)
    setTempTaskName(taskName)
    setNameHasError(false)
  }

  const onCancelClicked = () => {
    onClose(true)
    resetData()
  }
  const onSelectRobotClick = () => {
    setShowRobotModal(true)
  }

  const onSelectMissionClick = () => {
    setShowMissionModal(true)
  }

  const onMissionSelected = async (mission: IMissionInfo | any | undefined) => {
    setShowMissionModal(false)
    if (mission.is_scenario) {
      setSelectedScenario(mission)
      setSelectedMission(undefined)
    } else {
      setSelectedScenario(undefined)
      setSelectedMission(mission)
    }
    setTempTaskName(mission.name)
  }

  const onRobotSelected = async (robot: RobotRead | undefined) => {
    setShowRobotModal(false)
    setSelectedRobot(robot)
  }

  const getDiagnosticStatusClass = createDiagnosticStatusClassGetter(diagnosticStyles)

  const hasSystemDiagnostics = () => {
    return selectedRobot?.system_diagnostics !== null && selectedRobot?.system_diagnostics !== undefined
  }

  const shouldShowDiagnostics = () => {
    return (
      hasSystemDiagnostics() &&
      selectedRobot?.system_diagnostics &&
      shouldShowDiagnosticsUtil(selectedRobot.system_diagnostics.level)
    )
  }

  useEffect(() => {
    if (!isOpen || !selectedRobot?.id) {
      return
    }

    const robotId = selectedRobot.id

    const updateRobotData = async () => {
      try {
        const robot = await MainService.mainRobotRetrieve(robotId)
        setSelectedRobot(robot)
      } catch (error) {
        console.error('[TaskSettingsModal] Failed to update robot data:', error)
      }
    }

    const initialTimeout = setTimeout(() => {
      updateRobotData()
    }, 1000)

    const interval = setInterval(() => {
      updateRobotData()
    }, ROBOT_DIAGNOSTICS_POLLING_INTERVAL)

    return () => {
      clearTimeout(initialTimeout)
      clearInterval(interval)
    }
  }, [isOpen, selectedRobot?.id])

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onCancelClicked}
      className={style.root}
      style={{
        overlay: {
          zIndex: 100,
          backgroundColor: 'rgba(27, 43, 72, 0.6)',
        },
      }}
      ariaHideApp={false}
    >
      <div className={style.modal}>
        <div className={style.head}>
          <Title text={taskToEdit ? 'Edit the scheduled task' : 'Add a new task'} className={style.title} />
        </div>
        <Tabs
          value={tab}
          onChange={(e, newValue: number) => {
            setTab(newValue)
          }}
          variant="fullWidth"
          indicatorColor="primary"
          sx={{ height: 45, minHeight: 45, marginLeft: '48px', marginRight: '48px' }}
          centered
        >
          <StyledTab
            label="Main"
            sx={{ fontSize: 16, fontWeight: '400', textTransform: 'none', letterSpacing: 0 }}
            disableRipple
          />
          {!isLaunchMode && (
            <StyledTab
              label={'Schedule'}
              sx={{ fontSize: 16, fontWeight: '400', textTransform: 'none', letterSpacing: 0 }}
              disableRipple
            />
          )}
        </Tabs>
        <TabPanel value={tab} index={0} disablePadding={true}>
          <div className={style.content}>
            <TextFieldDescribed
              className={style.textfieldFullWidth}
              description={'Task name'}
              error={'Invalid task name'}
              placeholder={'Task name'}
              hasError={hasNameError}
              value={tempTaskName}
              onChange={onTaskNameChanged}
            />
            <div className={style.selectBlock}>
              <TextFieldDescribed
                className={style.textfieldFullWidth}
                description={'Robot'}
                placeholder={'Not selected'}
                value={
                  selectedRobot && selectedRobot.serial_number
                    ? `SN: ${selectedRobot.serial_number}, Name: ${selectedRobot.name}`
                    : 'Not selected'
                }
                disabled
                onChange={() => {}}
              />
              {
                <Button.Outlined className={style.btnSelect} onClick={onSelectRobotClick}>
                  Select a Robot
                </Button.Outlined>
              }
              <SelectRobotModal
                isOpen={showRobotModal}
                // Show all robots if there is no a mission
                robotType={selectedMission || selectedScenario ? robot_type : undefined}
                includeVendor={VENDOR.AUTONOMICS}
                onClose={() => setShowRobotModal(false)}
                onConfirmClick={onRobotSelected}
                robotIdsFilter={robotIdsFilter}
              />
            </div>
            {shouldShowDiagnostics() && selectedRobot?.system_diagnostics && (
              <div className={style.diagnosticsBlock}>
                <div className={style.diagnosticsLabel}>System Diagnostics</div>
                <div className={style.diagnosticsContent}>
                  <div
                    className={clsx(
                      style.diagnosticsChip,
                      getDiagnosticStatusClass(selectedRobot.system_diagnostics.level)
                    )}
                  >
                    {getDiagnosticStatusText(selectedRobot.system_diagnostics.level)}
                  </div>
                  <div className={style.diagnosticsMessage}>{selectedRobot.system_diagnostics.message || '-'}</div>
                </div>
              </div>
            )}
            {(() => {
              const mission = selectedScenario || selectedMission
              const is_scenario = selectedScenario || (taskToEdit as OdysseyRead)?.scenario || mission?.is_scenario

              return (
                <div key="single-mission">
                  <div className={style.missionRowContainer}>
                    <div className={style.selectBlock}>
                      <TextFieldDescribed
                        className={style.textfieldFullWidth}
                        description={is_scenario ? `Scenario` : mission ? `Mission` : `Mission/Scenario`}
                        placeholder={'Not selected'}
                        value={mission && mission.id ? `ID: ${mission.id}, Name: ${mission.name}` : 'Not selected'}
                        disabled
                        onChange={() => {}}
                      />
                      <Button.Outlined
                        className={style.btnSelect}
                        onClick={onSelectMissionClick}
                        disabled={isLaunchMode}
                      >
                        Mission/Scenario
                      </Button.Outlined>
                    </div>
                  </div>
                </div>
              )
            })()}
            <SelectMissionModal
              isOpen={showMissionModal}
              robotType={robot_type}
              showScenariosTab={true}
              hideInconsistentScenarios={true}
              onClose={() => setShowMissionModal(false)}
              onConfirmClick={onMissionSelected}
            />
          </div>
        </TabPanel>
        {!isLaunchMode && (
          <TabPanel value={tab} index={1} disablePadding={true}>
            <div className={style.content}>
              <SchedulerSettings
                initialDate={date}
                initialEndDate={endDate}
                initialWeekdays={selectedWeekdays}
                showWeekdaysSelection={!isSingleScope}
                onSchedulerChange={handleSchedulerChange}
              />
            </div>
          </TabPanel>
        )}
        <div className={style.footer}>
          {shouldBlockSave() && (
            <div className={style.warningBlock}>
              Cannot send task to robot due to diagnostics. You can only schedule the task.
            </div>
          )}
          <div className={style.footerButtons}>
            <Button.Outlined className={style.btn} onClick={onCancelClicked}>
              {t('mission_settings.label_cancel')}
            </Button.Outlined>
            <Button.Standart disabled={isSaveButtonDisabled} className={style.btn} onClick={onSaveClicked}>
              {t('mission_settings.label_save')}
            </Button.Standart>
          </div>
        </div>
        <ConfirmDialog
          isOpen={isConfirmationOpen}
          onClose={() => setIsConfirmationOpen(false)}
          onConfirmClick={handleConfirmSave}
          title="The task doesn't have a schedule. Execute immediately?"
        />
      </div>
    </Modal>
  )
}

export default TaskSettingsModal
